import { ReportsCategorySection } from './ReportsCategorySection';
import { ReportsMasthead } from './ReportsMasthead';
import { ReportsPeriodTabs } from './ReportsPeriodTabs';
import { ReportsStatTrio } from './ReportsStatTrio';
import { ReportsTransactionsSection } from './ReportsTransactionsSection';
import { ReportsTrendSection } from './ReportsTrendSection';
import { buildMastheadVolume } from './usePeriodState';
import type { ReportsViewActions, ReportsViewData } from './types';

export interface ReportsViewProps extends ReportsViewData, ReportsViewActions {
  /** Optional title override for admin "preview" labelling. Defaults to "The Reports". */
  title?: React.ReactNode;
  /** Optional subtitle override (e.g. admin: "Reviewer preview of …"). */
  subtitle?: string;
  /** Optional override of the masthead VOL/ISSUE line. */
  mastheadVolume?: string;
  /**
   * Outer wrapper className. SME page passes `min-h-full bg-paper` for the
   * full-bleed editorial look; admin tab passes nothing so the page sits flat
   * inside the OrgDetailPage tab container (which has its own background).
   */
  outerClassName?: string;
}

export function ReportsView({
  period,
  setPeriod,
  customRange,
  applyCustomRange,
  periodLabels,
  visiblePeriods,
  periodLabel,
  summary,
  categories,
  taxEstimate,
  trend,
  transactions,
  loading,
  refreshing,
  error,
  isDownloadingPdf,
  isDownloadingCsv,
  isSendingEmail,
  onDownloadPdf,
  onDownloadCsv,
  onSendEmail,
  title,
  subtitle,
  mastheadVolume,
  outerClassName = 'min-h-full bg-paper',
}: ReportsViewProps) {
  const needsCustomRange = period === 'custom' && !customRange;
  const computedMasthead = mastheadVolume ?? buildMastheadVolume(periodLabel);

  return (
    <div className={outerClassName}>
      <div className="max-w-310 mx-auto px-8 pt-10 pb-16">
        <ReportsMasthead
          mastheadVolume={computedMasthead}
          title={title}
          subtitle={subtitle}
          loading={loading}
          needsCustomRange={needsCustomRange}
          isDownloadingPdf={isDownloadingPdf}
          isDownloadingCsv={isDownloadingCsv}
          isSendingEmail={isSendingEmail}
          onDownloadPdf={onDownloadPdf}
          onDownloadCsv={onDownloadCsv}
          onSendEmail={onSendEmail}
        />

        <ReportsPeriodTabs
          period={period}
          setPeriod={setPeriod}
          visiblePeriods={visiblePeriods}
          periodLabels={periodLabels}
          periodLabel={periodLabel}
          customRange={customRange}
          applyCustomRange={applyCustomRange}
          refreshing={refreshing}
        />

        {error != null && (
          <div
            role="alert"
            className="mb-8 p-4 rounded-[10px] border border-neg/30 bg-neg/5 text-neg text-[13px]"
          >
            We couldn't load these reports. Refresh to try again.
          </div>
        )}

        <ReportsStatTrio summary={summary} loading={loading} />

        <ReportsCategorySection
          categories={categories}
          taxEstimate={taxEstimate}
          loading={loading}
        />

        <ReportsTrendSection trend={trend} loading={loading} />

        <ReportsTransactionsSection
          transactions={transactions}
          periodLabel={periodLabel}
          loading={loading}
        />
      </div>
    </div>
  );
}
